import React, { useCallback, useId, useState } from "react";
import { ActionList, Box, Button, FormLayout, Icon, Label, LegacyCard, LegacyStack, Popover, SkeletonBodyText, Text, TextField, Tooltip } from "@shopify/polaris";
import { AlertTriangleIcon, MenuHorizontalIcon } from "@shopify/polaris-icons";
import type { IPurchaseOrder, PurchaseOrderPaymentAttributes } from "../../../api_utils/types";
import { formatMoney, getCurrencySymbol, roundToTwoDecimals } from "../../../helper_functions/utils";
import CustomDatePicker from "../../common/CustomDatePicker/CustomDatePicker";
import { formatCalendarDate } from "../../common/CustomDatePicker/utils";

export default function Payment({
  purchaseOrder,
  total,
  loading,
  disabled,
  onChange,
}: {
  readonly purchaseOrder: Pick<IPurchaseOrder, "amount_paid" | "payment_due_date" | "currency">;
  readonly total: number;
  readonly loading: boolean;
  readonly disabled: boolean;
  readonly onChange: (attributes: PurchaseOrderPaymentAttributes) => void;
}): React.JSX.Element {
  const amountPaidId = useId();
  const dueDateId = useId();
  const [actionsOpen, setActionsOpen] = useState(false);
  const [amountPaidInput, setAmountPaidInput] = useState<string | null>(null);
  const formattedAmountPaid = purchaseOrder.amount_paid === null ? "" : formatMoney(purchaseOrder.amount_paid, undefined, 2);
  const amountDue = roundToTwoDecimals(total);
  const amountRemaining = amountDue - Number(purchaseOrder.amount_paid);
  const dueDate = purchaseOrder.payment_due_date ? new Date(`${purchaseOrder.payment_due_date}T00:00:00`) : undefined;
  const handleAmountPaidChange = useCallback(
    (amountPaid: string): void => {
      const cleanAmountPaid = amountPaid.replace(/[^\d.-]/g, "");
      if (/^(?:\d+(?:\.\d{0,2})?)?$/.test(cleanAmountPaid)) {
        setAmountPaidInput(cleanAmountPaid);
        onChange({ amount_paid: cleanAmountPaid || null });
      }
    },
    [onChange],
  );
  const handleAmountPaidFocus = useCallback((): void => {
    setAmountPaidInput(formattedAmountPaid);
  }, [formattedAmountPaid]);
  const handleAmountPaidBlur = useCallback((): void => {
    setAmountPaidInput(null);
  }, []);
  const handleDueDateChange = useCallback(
    (selectedDate: Date): void => {
      onChange({ payment_due_date: formatCalendarDate(selectedDate) });
    },
    [onChange],
  );
  const handleDueDateClear = useCallback((): void => {
    onChange({ payment_due_date: null });
  }, [onChange]);
  const toggleActions = useCallback((): void => {
    setActionsOpen((open) => !open);
  }, []);
  const handleMarkAsPaid = useCallback((): void => {
    onChange({ amount_paid: amountDue.toFixed(2) });
    setActionsOpen(false);
  }, [onChange, amountDue]);

  return (
    <LegacyCard>
      <LegacyCard.Header title="Payment">
        <Popover
          activator={
            <Button accessibilityLabel="Payment actions" disabled={disabled} icon={MenuHorizontalIcon} onClick={toggleActions} variant="tertiary" />
          }
          active={actionsOpen}
          onClose={toggleActions}
        >
          <ActionList actionRole="menuitem" items={[{ content: "Mark as paid", disabled, onAction: handleMarkAsPaid }]} />
        </Popover>
      </LegacyCard.Header>
      <LegacyCard.Section>
        <FormLayout>
          <FormLayout.Group>
            <LegacyStack alignment="center" wrap={false}>
              <LegacyStack.Item fill>
                <Label id={amountPaidId}>Amount paid</Label>
              </LegacyStack.Item>
              <LegacyStack.Item>
                <Box width="120px">
                  <TextField
                    align="right"
                    autoComplete="off"
                    disabled={disabled}
                    id={amountPaidId}
                    inputMode="decimal"
                    label=""
                    onBlur={handleAmountPaidBlur}
                    onChange={handleAmountPaidChange}
                    onFocus={handleAmountPaidFocus}
                    prefix={getCurrencySymbol(purchaseOrder.currency)}
                    type="currency"
                    value={amountPaidInput ?? formattedAmountPaid}
                  />
                </Box>
              </LegacyStack.Item>
              <LegacyStack.Item>
                <Box width="22px">
                  {!loading && amountRemaining < 0 && (
                    <Tooltip content="Amount paid exceeds the purchase order total. You can still save this amount.">
                      <Icon accessibilityLabel="Amount paid warning" source={AlertTriangleIcon} tone="warning" />
                    </Tooltip>
                  )}
                </Box>
              </LegacyStack.Item>
            </LegacyStack>
          </FormLayout.Group>
          <FormLayout.Group>
            <LegacyStack alignment="center" wrap={false}>
              <LegacyStack.Item fill>
                <Text as="span" fontWeight="semibold">Amount remaining</Text>
              </LegacyStack.Item>
              <LegacyStack.Item>
                <Box aria-busy={loading} aria-label="Amount remaining" role="status" width="120px">
                  {loading ? (
                    <SkeletonBodyText lines={1} />
                  ) : (
                    <Text alignment="end" as="p">{formatMoney(amountRemaining, purchaseOrder.currency, 2)}</Text>
                  )}
                </Box>
              </LegacyStack.Item>
              <LegacyStack.Item>
                <Box width="32px" />
              </LegacyStack.Item>
            </LegacyStack>
          </FormLayout.Group>
          <FormLayout.Group>
            <LegacyStack alignment="center" wrap={false}>
              <LegacyStack.Item fill>
                <Label id={dueDateId}>Payment due date</Label>
              </LegacyStack.Item>
              <LegacyStack.Item>
                <Box width="160px">
                  <CustomDatePicker
                    date={dueDate}
                    disabled={disabled}
                    id={dueDateId}
                    label=""
                    onClear={handleDueDateClear}
                    placeholder="Select date"
                    setDate={handleDueDateChange}
                  />
                </Box>
              </LegacyStack.Item>
              <LegacyStack.Item>
                <Box width="22px" />
              </LegacyStack.Item>
            </LegacyStack>
          </FormLayout.Group>
        </FormLayout>
      </LegacyCard.Section>
    </LegacyCard>
  );
}
